/**
 * Helper class for currency-related operations
 */
export class CurrencyHelper {
  /**
   * Format a number as currency with the specified currency code
   * @param value - The number to format
   * @param currencyCode - Optional currency code (defaults to stored currency or USD)
   * @returns Formatted currency string
   */
  public static formatCurrency(value: any): string {
    if (!value || value === 'null') { return '-'; }

    // Get currency code from localStorage or default to USD
    const currencyCode = localStorage.getItem('currency') || 'USD';

    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) { return '-'; }

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(numValue);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return '-';
    }
  }

  /**
   * Get currency symbol from currency code
   * @param currencyCode - The currency code (e.g., 'USD', 'EUR')
   * @returns Currency symbol
   */
  public static getCurrencySymbol(currencyCode: string = 'USD'): string {
    try {
      const symbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      })
      .format(0)
      .replace(/[0-9.,\s]/g, '');

      return symbol;
    } catch (error) {
      console.error('Error getting currency symbol:', error);
      return currencyCode;
    }
  }

  /**
   * Format a number as currency with a specific symbol
   * @param value - The number to format
   * @param currencyCode - The currency code to use for formatting
   * @returns Formatted currency string with symbol
   */
  public static formatWithSymbol(value: any, currencyCode: string): string {
    if (!value || value === 'null') { return '-'; }

    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) { return '-'; }

      const symbol = this.getCurrencySymbol(currencyCode);
      return `${symbol}${numValue.toFixed(2)}`;
    } catch (error) {
      console.error('Error formatting currency with symbol:', error);
      return '-';
    }
  }

  /**
   * Format a number as currency without symbol
   * @param value - The number to format
   * @returns Formatted number with 2 decimal places
   */
  public static formatNumberOnly(value: any): string {
    if (!value || value === 'null') { return '0.00'; }

    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) { return '0.00'; }

      return numValue.toFixed(2);
    } catch (error) {
      console.error('Error formatting number:', error);
      return '0.00';
    }
  }

  /**
   * Format a number with comma separators (rounded to integer)
   * @param value - The number to format
   * @returns Formatted number string with comma separators
   */
  public static formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) { return '0'; }
    if (Number.isNaN(value)) { return '0'; }
    return Math.round(value).toLocaleString();
  }

  /**
   * Format a number as percentage
   * @param value - The number to format (e.g., 45.5 for 45.5%)
   * @returns Formatted percentage string
   */
  public static formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) { return '0%'; }
    if (Number.isNaN(value)) { return '0%'; }
    return `${value.toFixed(1)}%`;
  }

  /**
   * Get color for units sold based on value thresholds
   * @param units - Number of units sold
   * @returns Vuetify color name
   */
  public static getUnitsColor(units: number): string {
    if (!Number.isFinite(units)) { return 'grey'; }
    if (units === 0) { return 'grey'; }
    if (units < 10) { return 'orange'; }
    if (units < 50) { return 'blue'; }
    return 'primary';
  }

  /**
   * Get color for margin percentage based on value thresholds
   * @param margin - Margin percentage
   * @returns Vuetify color name
   */
  public static getMarginColor(margin: number): string {
    if (!Number.isFinite(margin)) { return 'grey'; }
    if (margin < 0) { return 'error'; }
    if (margin < 20) { return 'warning'; }
    if (margin < 50) { return 'info'; }
    return 'success';
  }
}
